'use client';

import type { InputProps } from '../input';

import { forwardRef, useRef, useState } from 'react';

import { useCursor } from '@paid-ui/hooks/use-cursor';
import { useIsomorphicEffect } from '@paid-ui/hooks/use-isomorphic-effect';
import { useMergedRef } from '@paid-ui/hooks/use-merged-ref';
import { formatABN } from '@paid-ui/utils/abn';

import { Input } from '../input';

const MAX_LENGTH = 14;
const INPUT_REGEXP = /^[\d\s]{0,14}$/;
const REPLACE_REGEXP = /\D+/g;
const cleanValue = (value: string) => value.replaceAll(REPLACE_REGEXP, '').slice(0, 11);

export interface ABNInputProps
  extends Omit<
    InputProps,
    'type' | 'inputMode' | 'maxLength' | 'autoComplete' | 'autoCapitalize' | 'autoCorrect'
  > {
  value?: string;
  defaultValue?: string;
}

export const ABNInput = forwardRef<HTMLInputElement, ABNInputProps>(
  ({ value, defaultValue, onChange, onFocus, onBlur, onPaste, ...restProps }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState(false);
    const [recordCursor, restoreCursor] = useCursor(inputRef.current, focus);
    const [displayValue, setDisplayValue] = useState(() => formatABN(value ?? defaultValue) ?? '');

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(true);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onFocus?.(event);
      setDisplayValue(formatABN(targetValue) ?? '');
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setFocus(false);
      const targetValue = cleanValue(event.currentTarget.value);
      event.currentTarget.value = targetValue;
      onBlur?.(event);
      setDisplayValue(formatABN(targetValue) ?? '');
    };

    const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = (event) => {
      const pastedValue = event.clipboardData.getData('text/plain');
      const targetValue = cleanValue(pastedValue);
      event.currentTarget.value = targetValue;
      onPaste?.(event);
      setDisplayValue(formatABN(targetValue) ?? '');
      // @ts-expect-error - Event type is not compatible.
      onChange?.(event);
      event.preventDefault();
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      // 1. Record the cursor position and related values.
      recordCursor();

      // 2. Get cleaned input value and formatted display value.
      const targetValue = cleanValue(event.currentTarget.value);
      const newDisplayValue = formatABN(targetValue) ?? '';

      // 3. If the input is empty, set the display value to empty and call onChange.
      if (!targetValue) {
        setDisplayValue('');
        onChange?.(event);
        return;
      }

      // 4. If the input is invalid, do nothing.
      // 5. If th new display value is the same as the old one, do nothing.
      if (!INPUT_REGEXP.test(targetValue) || newDisplayValue === displayValue) {
        return;
      }

      // 6. Set the display value to the new one and call onChange.
      setDisplayValue(newDisplayValue);
      event.currentTarget.value = targetValue;
      onChange?.(event);
    };

    useIsomorphicEffect(() => {
      // 7. Restore the cursor position if the display value has changed.
      restoreCursor();
    }, [displayValue]);

    // The rendered text is local state, so a value written from outside — a form reset, or a
    // quick fix that replaces the ABN — would otherwise leave the stale text on screen. Skip
    // while focused: what comes back mid-typing is the value this input just emitted.
    useIsomorphicEffect(() => {
      if (focus || value === undefined) {
        return;
      }
      setDisplayValue(formatABN(cleanValue(value)) ?? '');
    }, [value, focus]);

    return (
      <Input
        ref={useMergedRef(inputRef, ref)}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        {...restProps}
        type="text"
        inputMode="numeric"
        maxLength={MAX_LENGTH}
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
      />
    );
  },
);

ABNInput.displayName = 'ABNInput';
